/* eslint-disable react/require-default-props */
import React, { useCallback, useContext, useMemo, useReducer } from "react";
import { useDropzone } from "react-dropzone";
import {
  ProcessedFile,
  BulkUploadReducerAction,
  constructPFSMetadata,
  defaultBulkUploadContext,
  FileArrayItem,
  PfsApiMetadata,
} from "./CustomerRFQ.types";
import { useConfig } from "../proposals/contexts/configContext";

// TODO: Move all of these pure functions to a separate file
export function handleFetchResponse(response: Response): Promise<unknown> {
  return response.text().then(text => {
    const data = text && JSON.parse(text);

    if (!response.ok) {
      const error =
        (data && data.message) || `${response.status}: ${response.statusText}`;
      return Promise.reject(error);
    }

    return data;
  });
}

export function uploadPFSFile(
  file: File,
  userId: string,
  partName: string,
  pfsOrigin: string,
  additionalHeaders: Record<string, string> = {}
): Promise<PfsApiMetadata> {
  // Receives the file to upload, the user ID of the file owner
  // and the name of the file to upload.
  // Note: File owner should be the client user, or the quote owner if
  // a staff member is the client user
  const data = new FormData();
  data.append("json", JSON.stringify(constructPFSMetadata(userId, partName)));
  data.append("file", file);
  const pfsApi = `${pfsOrigin}/file`;
  const headers: Record<string, string> = {
    "X-Requested-With": "XMLHttpRequest",
    ...additionalHeaders,
  };

  // Form Data should not be sent with a content type
  // making sure we handle all kinds of casing for the key
  Object.keys(headers)
    .filter((key: string) => {
      return key.toLocaleLowerCase() === "content-type";
    })
    .forEach((key: string) => {
      delete headers[key];
    });

  return fetch(pfsApi, {
    method: "PUT",
    headers,
    credentials: "include",
    body: data,
  }).then(
    (response: Response) =>
      handleFetchResponse(response) as Promise<PfsApiMetadata>
  );
}

export const processFilesPure = (
  files: FileArrayItem[],
  userId: string,
  pfsOrigin: string,
  processedCallback: (processed: ProcessedFile, index: number) => void
): Array<Promise<ProcessedFile>> => {
  return files.map((file: FileArrayItem, index: number) => {
    if (file.error) {
      return Promise.reject(file.message);
    }
    if (file.processed && file.pfsId) {
      return Promise.resolve({
        fileId: file.pfsId,
        fileName: file.name,
      });
    }
    if (file.file) {
      return uploadPFSFile(file.file, userId, file.file.name, pfsOrigin).then(
        (response: PfsApiMetadata) => {
          if (response.id) {
            const processedFile = {
              fileId: response.id,
              fileName: file.file?.name,
            };
            processedCallback(processedFile, index);
            return processedFile;
          }
          return Promise.reject(new Error("No id found"));
        }
      );
    }
    if (file.id) {
      return Promise.resolve({
        fileId: file.id,
        fileName: file.name,
      });
    }
    return Promise.reject(new Error("No file or id found"));
  });
};

export const fileReducer = (
  files: FileArrayItem[],
  action: BulkUploadReducerAction
): FileArrayItem[] => {
  const returnVal = files;
  switch (action.type) {
    case "add":
      return [...returnVal, ...action.files];
    case "remove":
      returnVal.splice(action.index, 1);
      return [...returnVal];
    case "markProcessed":
      returnVal[action.index].processed = true;
      returnVal[action.index].pfsId = action.pfsId;
      return [...returnVal];
    case "removeFilter":
      return returnVal.filter(action.filter);
    case "clear":
      return [];
    default:
      return [...returnVal];
  }
};

export const BulkFileUploadPanelContext = React.createContext(
  defaultBulkUploadContext
);

export const useBulkUpload = () => {
  const bulkUpload = React.useContext(BulkFileUploadPanelContext);
  return bulkUpload;
};

const BulkUploadDropZone = ({
  children,
  onDropEvent,
}: {
  children: React.ReactNode;
  onDropEvent?: () => void;
}): React.ReactElement => {
  const {
    actions: { addFiles },
  } = useContext(BulkFileUploadPanelContext);
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (onDropEvent) {
        onDropEvent();
      }
      addFiles(acceptedFiles);
    },
    [addFiles, onDropEvent]
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
  });
  const extraConfig = useMemo(
    () => (isDragActive ? { className: "dropzone-drag-active" } : {}),
    [isDragActive]
  );

  // this is the official way to set the props on the dropzone
  /* eslint-disable react/jsx-props-no-spreading */
  return (
    <div {...getRootProps(extraConfig)}>
      <input {...getInputProps()} />
      {children}
    </div>
  );
  /* eslint-enable react/jsx-props-no-spreading */
};

export const BulkFileUploadPanelContextStore = ({
  children,
  userId,
}: {
  children: React.ReactNode;
  userId: string;
}): React.ReactElement => {
  const {
    origins: { partFilesService },
    onBehalfOfUser,
  } = useConfig();
  const pfsUserId = onBehalfOfUser?.userId || userId;
  const [files, stateFileReducer] = useReducer(
    fileReducer,
    [] as FileArrayItem[]
  );
  const addFiles = useCallback(
    (newFiles: File[] | File) => {
      const filesToAdd: FileArrayItem[] = [];
      const filesToProcess = Array.isArray(newFiles) ? newFiles : [newFiles];
      filesToProcess.forEach((file: File) => {
        filesToAdd.push({
          file,
          name: file.name,
          error: false,
          message: "",
        });
      });
      stateFileReducer({
        type: "add",
        files: filesToAdd,
      });
    },
    [stateFileReducer]
  );

  const removeFile = useCallback(
    (index: number) => {
      stateFileReducer({
        type: "remove",
        index,
      });
    },
    [stateFileReducer]
  );

  const clearFiles = useCallback(() => {
    stateFileReducer({
      type: "clear",
    });
  }, [stateFileReducer]);

  const processFiles = useCallback(
    () =>
      Promise.all(
        processFilesPure(
          files,
          pfsUserId,
          partFilesService,
          (processedFile: ProcessedFile, i: number) => {
            stateFileReducer({
              type: "markProcessed",
              index: i,
              pfsId: processedFile.fileId as string,
            });
          }
        )
      ),
    [files, partFilesService, pfsUserId]
  );

  const fileListHandler = (fileList: FileList | null | undefined) => {
    const filesToProcess = Array.from(fileList || []);
    if (filesToProcess.length > 0) {
      addFiles(filesToProcess);
    }
  };

  const fileInput = React.createRef<HTMLInputElement>();

  const getFileInput = useCallback(() => {
    return fileInput.current;
  }, [fileInput]);

  const showFileSelector = useCallback(() => {
    fileInput.current?.click();
  }, [fileInput]);

  const withDropZone = (
    Wrapped: React.ComponentType<Record<string, any>>,
    onDropEvent?: () => void
  ) => {
    const WrappedWithDropZone = (props: Record<string, any>) => {
      /* eslint-disable react/jsx-props-no-spreading */
      return (
        <BulkUploadDropZone onDropEvent={onDropEvent}>
          <Wrapped {...props} />
        </BulkUploadDropZone>
      );
      /* eslint-enable react/jsx-props-no-spreading */
    };
    WrappedWithDropZone.displayName = "WrappedWithDropZone";
    return WrappedWithDropZone;
  };

  const value = {
    data: {
      files,
    },
    actions: {
      processFiles,
      addFiles,
      removeFile,
      clearFiles,
      showFileSelector,
      getFileInput,
      withDropZone,
    },
  };

  return (
    <BulkFileUploadPanelContext.Provider value={value}>
      <input
        multiple
        onChange={() => fileListHandler(fileInput.current?.files)}
        style={{ display: "none" }}
        type="file"
        ref={fileInput}
      />
      {children}
    </BulkFileUploadPanelContext.Provider>
  );
};

export default BulkFileUploadPanelContextStore;
